import { useState } from 'react';

interface Sport {
  id: string;
  name: string;
  icon: string;
  color: string;
  bgGradient: string;
  image: string;
  stats: {
    label: string;
    value: string;
    icon: string;
  }[];
  features: string[];
  demoText: string;
}

export default function SportsDemo() {
  const [activeSport, setActiveSport] = useState<string>('football');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');

  const sports: Sport[] = [
    {
      id: 'football',
      name: 'Football',
      icon: 'ri-football-line',
      color: 'teal',
      bgGradient: 'from-teal-500 to-teal-600',
      image: 'https://public.readdy.ai/ai/img_res/4ba19b93edbce884d56bf71e96e67b9d.jpg',
      stats: [
        { label: 'Teams Managed', value: '2,400+', icon: 'ri-team-line' },
        { label: 'Equipment Orders', value: '$12M+', icon: 'ri-shopping-cart-line' },
        { label: 'Time Saved', value: '18hr/wk', icon: 'ri-time-line' }
      ],
      features: [
        'Full uniform and protective gear management',
        'Helmet safety compliance tracking',
        'Automatic cleat and pad ordering',
        'Real-time commission on NFL jersey sales'
      ],
      demoText: 'Manage your entire football program equipment from helmets to jerseys with automated ordering and safety compliance tracking.'
    },
    {
      id: 'basketball',
      name: 'Basketball',
      icon: 'ri-basketball-line',
      color: 'orange',
      bgGradient: 'from-orange-500 to-orange-600',
      image: 'https://readdy.ai/api/search-image?query=professional%20basketball%20players%20in%20dynamic%20action%20during%20intense%20game%20with%20slam%20dunk%20and%20fast%20break%2C%20athletic%20motion%20on%20indoor%20court%20with%20dramatic%20arena%20lighting%2C%20multiple%20players%20in%20modern%20jerseys%20competing%20with%20high%20energy%2C%20exciting%20sports%20photography%20showing%20teamwork%20and%20competitive%20basketball%20gameplay&width=1200&height=600&seq=basketball-demo-action&orientation=landscape',
      stats: [
        { label: 'Courts Equipped', value: '3,800+', icon: 'ri-basketball-line' },
        { label: 'Uniform Orders', value: '$8.5M+', icon: 'ri-shirt-line' },
        { label: 'Season Prep', value: '2 days', icon: 'ri-calendar-check-line' }
      ],
      features: [
        'Complete uniform and shoe inventory',
        'Ball and equipment tracking',
        'Custom jersey design ordering',
        'Training gear automation'
      ],
      demoText: 'Streamline basketball equipment from shoes to uniforms with one-click ordering and automated inventory management.'
    },
    {
      id: 'baseball',
      name: 'Baseball',
      icon: 'ri-football-fill',
      color: 'red',
      bgGradient: 'from-red-500 to-red-600',
      image: 'https://readdy.ai/api/search-image?query=professional%20baseball%20players%20in%20action%20during%20exciting%20game%20moment%20with%20pitcher%20throwing%20fastball%20and%20batter%20ready%20to%20swing%2C%20dynamic%20athletic%20motion%20on%20outdoor%20diamond%20field%20with%20stadium%20lights%2C%20multiple%20players%20in%20classic%20white%20and%20red%20uniforms%2C%20dramatic%20sports%20photography%20capturing%20competitive%20baseball%20gameplay%20and%20teamwork&width=1200&height=600&seq=baseball-demo-action&orientation=landscape',
      stats: [
        { label: 'Diamonds Managed', value: '4,200+', icon: 'ri-community-line' },
        { label: 'Bat Orders', value: '$6.2M+', icon: 'ri-store-line' },
        { label: 'Glove Tracking', value: '98% Rate', icon: 'ri-check-double-line' }
      ],
      features: [
        'Bat, glove, and helmet management',
        'Uniform and cleat ordering',
        'Safety equipment compliance',
        'Custom team gear options'
      ],
      demoText: 'Handle all baseball equipment needs from bats to gloves with smart tracking and automated vendor ordering.'
    },
    {
      id: 'soccer',
      name: 'Soccer',
      icon: 'ri-basketball-fill',
      color: 'green',
      bgGradient: 'from-green-500 to-green-600',
      image: 'https://readdy.ai/api/search-image?query=professional%20soccer%20players%20in%20dynamic%20action%20during%20intense%20match%20with%20players%20dribbling%20and%20competing%20for%20ball%2C%20athletic%20motion%20on%20grass%20field%20with%20dramatic%20stadium%20lighting%2C%20multiple%20athletes%20in%20modern%20green%20and%20white%20jerseys%20showing%20teamwork%2C%20exciting%20sports%20photography%20capturing%20competitive%20football%20gameplay%20and%20athletic%20skill&width=1200&height=600&seq=soccer-demo-action&orientation=landscape',
      stats: [
        { label: 'Clubs Served', value: '5,600+', icon: 'ri-global-line' },
        { label: 'Boot Orders', value: '$9.8M+', icon: 'ri-footprint-line' },
        { label: 'Kit Delivery', value: '3-5 days', icon: 'ri-truck-line' }
      ],
      features: [
        'Complete kit and boot inventory',
        'Shin guard and protective gear',
        'Training equipment ordering',
        'International shipping support'
      ],
      demoText: 'Manage soccer kits, boots, and training gear with global vendor network and fast international shipping.'
    },
    {
      id: 'hockey',
      name: 'Hockey',
      icon: 'ri-ping-pong-line',
      color: 'blue',
      bgGradient: 'from-blue-500 to-blue-600',
      image: 'https://readdy.ai/api/search-image?query=professional%20ice%20hockey%20players%20in%20intense%20action%20during%20fast%20paced%20game%20with%20players%20skating%20and%20competing%20for%20puck%2C%20dynamic%20athletic%20motion%20on%20ice%20rink%20with%20dramatic%20arena%20lighting%2C%20multiple%20athletes%20in%20modern%20blue%20and%20white%20jerseys%20and%20protective%20gear%2C%20exciting%20sports%20photography%20showing%20competitive%20hockey%20gameplay%20and%20teamwork&width=1200&height=600&seq=hockey-demo-action&orientation=landscape',
      stats: [
        { label: 'Rinks Managed', value: '1,800+', icon: 'ri-snowflake-line' },
        { label: 'Skate Orders', value: '$7.3M+', icon: 'ri-shopping-bag-line' },
        { label: 'Safety Rating', value: '5-Star', icon: 'ri-shield-star-line' }
      ],
      features: [
        'Skate, stick, and puck management',
        'Protective equipment tracking',
        'Helmet safety certification',
        'Cold-weather gear ordering'
      ],
      demoText: 'Control all hockey equipment from skates to protective gear with advanced safety tracking and vendor management.'
    }
  ];

  const activeSportData = sports.find(s => s.id === activeSport) || sports[0];

  const handleSportChange = (sportId: string) => {
    if (sportId !== activeSport) {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveSport(sportId);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleDemoClick = (videoUrl: string) => {
    setCurrentVideoUrl(videoUrl);
    setShowVideoModal(true);
  };

  const sportVideos = {
    Football: 'https://www.youtube.com/embed/FmvUm_KNR3k',
    Basketball: 'https://www.youtube.com/embed/jTBQyItE3_c',
    Baseball: 'https://www.youtube.com/embed/_uqGrTXwY20',
    Soccer: 'https://www.youtube.com/embed/dtQ3ULOXTG4',
    Hockey: 'https://www.youtube.com/embed/dtQ3ULOXTG4'
  };

  return (
    <>
      <section className="bg-white py-32" id="demo">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-100 rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-100 rounded-full filter blur-3xl opacity-20"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-teal-500 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-bold mb-4">
              INTERACTIVE SPORTS DEMO
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              See TeamPro in <span className={`bg-gradient-to-r ${activeSportData.bgGradient} text-transparent bg-clip-text`}>Action</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Select your sport and discover how TeamPro revolutionizes equipment management for teams at every level
            </p>
          </div>

          {/* Sport Selector */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {sports.map(sport => (
              <button
                key={sport.id}
                onClick={() => handleSportChange(sport.id)}
                className={`group px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 flex items-center space-x-3 whitespace-nowrap cursor-pointer ${
                  activeSport === sport.id
                    ? `bg-gradient-to-r ${sport.bgGradient} text-white shadow-xl scale-105`
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-gray-300 hover:scale-105'
                }`}
              >
                <div className={`w-8 h-8 flex items-center justify-center ${activeSport === sport.id ? '' : 'text-gray-500'}`}>
                  <i className={`${sport.icon} text-2xl`}></i>
                </div>
                <span>{sport.name}</span>
                {activeSport === sport.id && (
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                )}
              </button>
            ))}
          </div>

          {/* Demo Content */}
          <div className={`transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left: Image and Stats */}
              <div className="space-y-6">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                  <img
                    src={activeSportData.image}
                    alt={`${activeSportData.name} action`}
                    className="w-full h-96 object-cover transform group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://readdy.ai/api/search-image?query=professional%20$%7BactiveSportData.name.toLowerCase%28%29%7D%20players%20in%20action%20during%20game%20with%20dramatic%20lighting%20and%20athletic%20motion&width=1200&height=600&seq=${activeSportData.id}-backup&orientation=landscape`;
                    }}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${activeSportData.bgGradient} opacity-30`}></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className={`bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl`}>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center space-x-3">
                        <div className={`w-10 h-10 flex items-center justify-center bg-gradient-to-r ${activeSportData.bgGradient} rounded-xl`}>
                          <i className={`${activeSportData.icon} text-2xl text-white`}></i>
                        </div>
                        <span>{activeSportData.name} Management</span>
                      </h3>
                      <p className="text-gray-600">{activeSportData.demoText}</p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  {activeSportData.stats.map((stat, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 hover:border-teal-200 transition-all hover:scale-105 cursor-pointer"
                    >
                      <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-r ${activeSportData.bgGradient} rounded-xl mb-3`}>
                        <i className={`${stat.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Features and CTA */}
              <div className="space-y-8">
                <div className="bg-white rounded-3xl p-10 shadow-xl border-2 border-gray-100">
                  <h3 className="text-3xl font-bold text-gray-900 mb-6 flex items-center space-x-3">
                    <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-r ${activeSportData.bgGradient} rounded-xl`}>
                      <i className="ri-star-line text-2xl text-white"></i>
                    </div>
                    <span>Key Features</span>
                  </h3>

                  <div className="space-y-4 mb-8">
                    {activeSportData.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
                      >
                        <div className={`w-8 h-8 flex items-center justify-center bg-gradient-to-r ${activeSportData.bgGradient} rounded-lg flex-shrink-0 group-hover:scale-110 transition-transform`}>
                          <i className="ri-check-line text-xl text-white"></i>
                        </div>
                        <p className="text-gray-700 font-medium pt-1">{feature}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-4 mt-8">
                    <a
                      href="#equipment-store"
                      className="px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all flex items-center space-x-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-xl"></i>
                      <span>Try {activeSportData.name} Demo</span>
                    </a>
                    <a
                      href="#equipment-store"
                      className="px-8 py-4 bg-transparent border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-teal-500 hover:text-teal-500 transition-all whitespace-nowrap cursor-pointer"
                    >
                      Schedule Live Demo
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-24 bg-gradient-to-r from-teal-500 to-teal-600 rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-orange-500/10"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Program?</h3>
              <p className="text-teal-50 text-lg mb-8 max-w-2xl mx-auto">
                Join 15,000+ teams already using TeamPro to save time and streamline equipment management
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="#equipment-store"
                  className="px-10 py-5 bg-black text-white text-lg font-bold rounded-lg hover:bg-gray-900 transition-all flex items-center space-x-3 whitespace-nowrap cursor-pointer"
                >
                  <span>Get Started Now</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>
                <a
                  href="#equipment-store"
                  className="px-10 py-5 bg-white text-teal-600 text-lg font-bold rounded-lg hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Watch Full Demo
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="relative w-full max-w-5xl bg-gray-900 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            
            <div className="aspect-video">
              <iframe
                src={`${currentVideoUrl}?autoplay=1`}
                title="Sport Demo Video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
